import { useEffect, useRef, useState } from 'react';
import { Flex, Typography } from 'antd';
import { unByKey } from 'ol/Observable';

import { useProject } from '@contexts/Project.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import { createTooltip } from '@utils/map/tooltip.overlay';
import { formatArea } from '@utils/measurementSystem';
import { formatNumber } from '@utils/helpers';
import { objectLabel } from '../utils/transformUtils';
import ColorDot from '../components/colorFilter/ColorDot';

const { Text } = Typography;

const TOOLTIP_ID = 'collection-dsp-tooltip';

// Fallback swatch for an object with no configured color.
const FALLBACK_COLOR = '#8c8c8c';

/**
 * Hover-tooltip body for one sampling plot: the plot's own footprint area, its
 * total tally, and a per-object breakdown of count + density.
 *
 * Mirrors the model DSP flow's `FootprintTooltip`, which shows the footprint
 * area and then one row per detection class with its count and hectare
 * equivalent. The per-object density is read from the derived
 * `hectare_equivalent:<objectKey>` stats key (see collectionDspToPlots) —
 * the collection endpoint doesn't return it.
 *
 * One element of the model tooltip has no counterpart here: its "Rough Estimate"
 * warning comes from the rotated-grid endpoint's `lrf_active` flag, which the
 * collection flow never fetches.
 */
const DspTooltipBody = ({ plot, colorByKey }) => {
	const objects = useCollectionStore(s => s.objects);
	const { count, area } = plot.stats;
	const [areaNum, areaUnit] = formatArea(area);

	return (
		<Flex vertical gap={2}>
			<Text strong style={{ fontSize: '0.8rem', color: '#fff' }}>
				{formatNumber(count)} objects
			</Text>
			<Text style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.85)' }}>
				Plot area: {areaNum} {areaUnit}
			</Text>
			{Object.entries(plot.detections).map(([objectKey, stats]) => {
				const perHa = plot.stats[`hectare_equivalent:${objectKey}`];
				return (
					<Text
						key={objectKey}
						style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.85)' }}>
						<ColorDot color={colorByKey[objectKey] ?? FALLBACK_COLOR} />
						{objectLabel(objects, objectKey)}: {formatNumber(stats.count)}
						{stats.count > 0 && perHa != null && (
							<span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
								{' '}
								/ {formatNumber(perHa, 0)} per ha
							</span>
						)}
					</Text>
				);
			})}
		</Flex>
	);
};

/**
 * Hover interactions for collection DSP plots, cloning the
 * `useCollectionHexagonTooltip` pattern (portal overlay element + an OL
 * `pointermove` listener) for the plot layer.
 *
 * Two differences from the hexagon version:
 *
 * - **Hover only.** The hexagon hook also binds `click` to select a cell; on the
 *   plot layer `singleclick` already opens the plot's image (see
 *   CollectionDspLayer), so binding selection here would fight it.
 * - **It doesn't defer to detection features.** The hexagon hook suppresses
 *   itself when a detection is under the cursor, because the detection tooltip
 *   owns that pixel. That tooltip is disabled in single-image mode
 *   (`CollectionTooltip`'s `enabled` gate), so deferring here would just leave
 *   the user with no tooltip at all over a detection.
 *
 * Refs hold values read inside the long-lived OL handler (the current plots and
 * colors) so the listener doesn't need re-registering on every data change.
 *
 * @param {boolean} enabled - gate (typically the plot layer's visibility)
 * @param {import('ol/layer/WebGLVector').default|null} layer - the plot layer
 * @param {import('../utils/collectionDspToPlots').DspPlot[]} plots
 * @param {Record<string, string>} colorByKey - object key (node uuid) → configured color
 * @param {React.RefObject<HTMLElement>} overlayElRef - imperatively-created overlay element
 * @returns {{ tooltipContent: React.ReactNode }}
 */
const useCollectionDspTooltip = (enabled, layer, plots, colorByKey, overlayElRef) => {
	const { mapObject, toolBarVisible, interactionsDisabled } = useProject();

	const plotsRef = useRef(plots);
	const colorByKeyRef = useRef(colorByKey);
	const toolBarVisibleRef = useRef(toolBarVisible);
	const interactionsDisabledRef = useRef(interactionsDisabled);
	useEffect(() => {
		plotsRef.current = plots;
		colorByKeyRef.current = colorByKey;
		toolBarVisibleRef.current = toolBarVisible;
		interactionsDisabledRef.current = interactionsDisabled;
	});

	const [tooltipContent, setTooltipContent] = useState(null);

	useEffect(() => {
		if (!mapObject || !enabled || !layer || !overlayElRef.current) return;

		const overlay = createTooltip({
			mapRef: mapObject,
			tooltipRef: overlayElRef.current,
			offset: [12, -12],
			positioning: 'bottom-left',
			id: TOOLTIP_ID,
		});

		// Resolve the plot under the cursor, scoped to the plot layer so this never
		// picks up detection or boundary features.
		const plotAt = pixel => {
			const feats = mapObject.getFeaturesAtPixel(pixel, { layerFilter: l => l === layer });
			const plotId = feats?.find(f => f.get('plotId') != null)?.get('plotId');
			if (plotId == null) return null;
			return plotsRef.current.find(p => p.id === plotId) ?? null;
		};

		const moveKey = mapObject.on('pointermove', e => {
			if (e.dragging || !toolBarVisibleRef.current || interactionsDisabledRef.current) {
				overlay.setPosition(undefined);
				return;
			}
			const plot = plotAt(e.pixel);
			if (!plot) {
				overlay.setPosition(undefined);
				return;
			}
			setTooltipContent(<DspTooltipBody plot={plot} colorByKey={colorByKeyRef.current} />);
			overlay.setPosition(e.coordinate);
		});

		return () => {
			unByKey(moveKey);
			mapObject.removeOverlay(overlay);
		};
	}, [mapObject, enabled, layer, overlayElRef]);

	return { tooltipContent };
};

export default useCollectionDspTooltip;
